import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  fetchAuctionSettings,
  fetchAuctionTeamSetup,
  fetchAuctionTeamPlayers,
} from '../../../services/tournamentService';
import { getAuctionManagerPath } from '../../../utils/organization';
import { getAuctionManagerPath as getCommunityAuctionManagerPath } from '../../../utils/community';
import { getAuctionManagerPath as getClubAuctionManagerPath } from '../../../utils/club';
import { buildTeamSlug, findTeamBySlug, normalizeAuctionTeams, normalizeTeamValueId, resolveTeamPlayer } from '../../../utils/teams';
import PageShell from '../../../components/layout/PageShell';

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const AuctionTeamDetailPage = () => {
  const { token } = useAuth();
  const { organizationId, communityId, clubId, auctionManagerId, tournamentId, teamSlug } = useParams();
  const auctionManagementPath =
    (communityId
      ? getCommunityAuctionManagerPath(communityId, auctionManagerId, tournamentId)
      : clubId
        ? getClubAuctionManagerPath(clubId, auctionManagerId, tournamentId)
        : getAuctionManagerPath(organizationId, auctionManagerId, tournamentId)) || '/management/auction';

  const [teamSetup, setTeamSetup] = useState(null);
  const [players, setPlayers] = useState([]);
  const [teamOwners, setTeamOwners] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [setupError, setSetupError] = useState('');
  const [teamOwnersError, setTeamOwnersError] = useState('');
  const [playersError, setPlayersError] = useState('');

  useEffect(() => {
    if (!token || !tournamentId) return;

    const loadData = async () => {
      setIsLoading(true);
      setSetupError('');
      setPlayersError('');

      try {
        const [setupResult, playersResult, settingsResult] = await Promise.allSettled([
          fetchAuctionTeamSetup(tournamentId, token),
          fetchAuctionTeamPlayers(tournamentId, token),
          fetchAuctionSettings(tournamentId, token),
        ]);

        if (setupResult.status === 'fulfilled') {
          setTeamSetup(setupResult.value.setup);
        } else {
          const message = setupResult.reason?.message || 'Unable to load team details right now.';
          setSetupError(message);
          setTeamSetup(null);
        }

        if (playersResult.status === 'fulfilled') {
          const resolvedPlayers = Array.isArray(playersResult.value?.players)
            ? playersResult.value.players
            : playersResult.value || [];
          setPlayers(resolvedPlayers);
        } else {
          const message = playersResult.reason?.message || 'Unable to load team players right now.';
          setPlayersError(message);
          setPlayers([]);
        }

        if (settingsResult.status === 'fulfilled') {
          const owners = Array.isArray(settingsResult.value?.settings?.teamOwners)
            ? settingsResult.value.settings.teamOwners
            : [];
          const normalizedOwners = owners.map((owner, index) => ({
            id:
              owner.admin?.id ||
              owner.admin?._id ||
              owner.id ||
              owner._id ||
              owner.email ||
              `owner-${index}`,
            name: owner.name || owner.admin?.name || '',
            email: owner.email || owner.admin?.email || '',
            phone: owner.phone || owner.admin?.phone || '',
            teamIndex: Number.isInteger(owner.teamIndex) ? owner.teamIndex : null,
          }));
          setTeamOwners(normalizedOwners);
          setTeamOwnersError('');
        } else {
          const message = settingsResult.reason?.message || 'Unable to load team owner details right now.';
          setTeamOwnersError(message);
          setTeamOwners([]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [token, tournamentId]);

  const displayTeams = useMemo(() => normalizeAuctionTeams(teamSetup), [teamSetup]);
  const resolvedPlayers = useMemo(
    () => (players || []).map((player) => player.player || player),
    [players]
  );

  const findPlayerById = useCallback((playerId) => resolveTeamPlayer(resolvedPlayers, playerId), [resolvedPlayers]);

  const activeTeam = useMemo(
    () => findTeamBySlug(displayTeams, teamSlug),
    [displayTeams, teamSlug]
  );
  const activeTeamIndex = useMemo(
    () => displayTeams.findIndex((team, index) => buildTeamSlug(team.name, index) === teamSlug),
    [displayTeams, teamSlug]
  );
  const activeTeamOwner = useMemo(() => {
    if (!Number.isInteger(activeTeamIndex) || activeTeamIndex < 0) return null;
    return teamOwners.find((owner) => owner.teamIndex === activeTeamIndex) || null;
  }, [activeTeamIndex, teamOwners]);

  const captain = activeTeam ? resolveTeamPlayer(resolvedPlayers, activeTeam.captain || activeTeam.captainRef || activeTeam.captainId) : null;
  const wicketKeeper = activeTeam ? resolveTeamPlayer(resolvedPlayers, activeTeam.wicketKeeper || activeTeam.wicketKeeperRef || activeTeam.wicketKeeperId) : null;
  const viceCaptain = activeTeam ? resolveTeamPlayer(resolvedPlayers, activeTeam.viceCaptain || activeTeam.viceCaptainRef || activeTeam.viceCaptainId) : null;

  const teamPlayers = useMemo(() => {
    if (!activeTeam) return [];
    const mappedTeamPlayers = (activeTeam.players || []).map((player, index) => {
      const playerId = normalizeTeamValueId(player);
      const resolvedPlayer = (player && player.player) || findPlayerById(playerId) || player || {};
      const name =
        resolvedPlayer.fullName ||
        resolvedPlayer.name ||
        resolvedPlayer.personal?.fullName ||
        'Unnamed player';
      const email = resolvedPlayer.email || resolvedPlayer.user?.email || 'Email not provided';
      const role = resolvedPlayer.primaryRole || resolvedPlayer.skills?.primaryRole || 'Role not specified';
      const avatar =
        resolvedPlayer.profilePhoto ||
        resolvedPlayer.media?.profilePhoto ||
        resolvedPlayer.image ||
        resolvedPlayer.photoUrl ||
        '';

      return {
        id: playerId || `${name}-${index}`,
        name,
        email,
        role,
        avatar,
        isOwner: Boolean(activeTeam?.ownerId && playerId && normalizeTeamValueId(activeTeam.ownerId) === normalizeTeamValueId(playerId)),
        isCaptain: Boolean((activeTeam?.captain || activeTeam?.captainRef || activeTeam?.captainId) && playerId && normalizeTeamValueId(activeTeam.captain || activeTeam.captainRef || activeTeam.captainId) === normalizeTeamValueId(playerId)),
      };
    });
    const includedIds = new Set(mappedTeamPlayers.map((player) => String(player.id)));

    const ownerId = normalizeTeamValueId(activeTeam?.ownerId);
    if (ownerId && !includedIds.has(ownerId)) {
      const ownerPlayer = findPlayerById(ownerId);
      if (ownerPlayer) {
        mappedTeamPlayers.unshift({
          id: ownerId,
          name: ownerPlayer.fullName || ownerPlayer.name || ownerPlayer.personal?.fullName || 'Unnamed player',
          email: ownerPlayer.email || ownerPlayer.user?.email || 'Email not provided',
          role: ownerPlayer.primaryRole || ownerPlayer.skills?.primaryRole || 'Role not specified',
          avatar:
            ownerPlayer.profilePhoto ||
            ownerPlayer.media?.profilePhoto ||
            ownerPlayer.image ||
            ownerPlayer.photoUrl ||
            '',
          isOwner: true,
          isCaptain: ownerId === normalizeTeamValueId(activeTeam?.captain || activeTeam?.captainRef || activeTeam?.captainId),
        });
        includedIds.add(ownerId);
      }
    }

    const captainId = normalizeTeamValueId(activeTeam?.captain || activeTeam?.captainRef || activeTeam?.captainId);
    if (captainId && !includedIds.has(captainId)) {
      const captainPlayer = findPlayerById(captainId);
      if (captainPlayer) {
        mappedTeamPlayers.unshift({
          id: captainId,
          name: captainPlayer.fullName || captainPlayer.name || captainPlayer.personal?.fullName || 'Unnamed player',
          email: captainPlayer.email || captainPlayer.user?.email || 'Email not provided',
          role: captainPlayer.primaryRole || captainPlayer.skills?.primaryRole || 'Role not specified',
          avatar:
            captainPlayer.profilePhoto ||
            captainPlayer.media?.profilePhoto ||
            captainPlayer.image ||
            captainPlayer.photoUrl ||
            '',
          isOwner: captainId === ownerId,
          isCaptain: true,
        });
      }
    }

    return mappedTeamPlayers;
  }, [activeTeam, findPlayerById]);

  const renderPlayerAvatar = (avatar, name) => (
    <img
      src={avatar || '/avatar.png'}
      alt={name}
      className="rounded-circle"
      width={56}
      height={56}
      style={{ objectFit: 'cover' }}
    />
  );

  const renderTeamMeta = () => {
    if (!activeTeam) return null;

    const playersSelected = activeTeam.players?.length || 0;
    const rosterTarget = activeTeam.playerCount || teamSetup?.playersPerTeam || 0;
    const teamInitial = activeTeam.name?.[0]?.toUpperCase?.() || 'T';

    return (
      <div className="card shadow-sm border-0">
        <div className="card-body d-flex flex-column gap-3">
          <div className="d-flex align-items-center gap-3">
            {activeTeam.logoUrl ? (
              <img
                src={activeTeam.logoUrl}
                alt={activeTeam.name}
                className="rounded"
                style={{ width: '80px', height: '80px', objectFit: 'cover' }}
              />
            ) : (
              <div
                className="rounded bg-secondary-subtle text-secondary-emphasis d-flex align-items-center justify-content-center"
                style={{ width: '80px', height: '80px' }}
              >
                <span className="fw-bold fs-4">{teamInitial}</span>
              </div>
            )}
            <div>
              <p className="eyebrow text-primary fw-semibold mb-1 text-uppercase small">Auction team</p>
              <h1 className="h4 mb-1">{activeTeam.name}</h1>
              <p className="text-muted mb-0">
                {playersSelected} / {rosterTarget || '—'} players selected · Slug: {buildTeamSlug(activeTeam.name)}
              </p>
            </div>
          </div>

          <div className="row g-3">
            {[
              { label: 'Captain', player: captain },
              { label: 'Vice captain', player: viceCaptain },
              { label: 'Wicket keeper', player: wicketKeeper },
              { label: 'Team owner', owner: activeTeamOwner },
            ].map(({ label, player, owner }) => (
              <div className="col-12 col-md-6 col-lg-3" key={label}>
                <div className="border rounded p-3 h-100 bg-light">
                  <p className="text-muted mb-1 small text-uppercase">{label}</p>
                  {player ? (
                    <div className="d-flex align-items-center gap-2">
                      {renderPlayerAvatar(
                        player.profilePhoto || player.media?.profilePhoto || player.image,
                        player.fullName || player.name,
                      )}
                      <div>
                        <p className="mb-0 fw-semibold">{player.fullName || player.name || 'Unnamed player'}</p>
                        <small className="text-muted">{player.primaryRole || player.skills?.primaryRole || 'Role'}</small>
                      </div>
                    </div>
                  ) : owner ? (
                    <div className="d-flex flex-column gap-1">
                      <p className="mb-0 fw-semibold">{owner.name || owner.email || 'Team owner'}</p>
                      {owner.email && <small className="text-muted">{owner.email}</small>}
                      {owner.phone && <small className="text-muted">{owner.phone}</small>}
                      {!owner.email && !owner.phone && <small className="text-muted">Contact not provided</small>}
                    </div>
                  ) : (
                    <p className="mb-0 text-muted">Not assigned</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          {teamOwnersError && (
            <p className="text-muted small mb-0">Team owner details unavailable: {teamOwnersError}</p>
          )}
        </div>
      </div>
    );
  };

  const renderPlayerList = () => {
    if (!activeTeam) return null;

    if (teamPlayers.length === 0) {
      return <p className="text-muted mb-0">No players have been assigned to this team yet.</p>;
    }

    return (
      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
        {teamPlayers.map((player) => (
          <div className="col" key={player.id}>
            <div className="border rounded p-3 h-100 d-flex gap-3 align-items-start">
              {renderPlayerAvatar(player.avatar, player.name)}
              <div className="d-flex flex-column gap-1">
                <p className="mb-0 fw-semibold">{player.name}</p>
                <div className="d-flex flex-wrap gap-1">
                  {player.isOwner ? <span className="badge text-bg-warning">Owner</span> : null}
                  {player.isCaptain ? <span className="badge text-bg-primary">Captain</span> : null}
                </div>
                <small className="text-muted">{player.role}</small>
                <small className="text-muted">{player.email}</small>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return <p className="text-muted">Loading team details…</p>;
    }

    if (setupError || playersError) {
      return <div className="alert alert-danger mb-0">{setupError || playersError}</div>;
    }

    if (!activeTeam) {
      return (
        <div className="alert alert-warning mb-0">
          <div className="d-flex flex-column gap-2">
            <p className="mb-0">The requested team could not be found.</p>
            <div className="d-flex gap-2">
              <Link to={`${auctionManagementPath}/online/start/shuffle`} className="btn btn-outline-primary btn-sm">
                Back to auction start
              </Link>
              <Link to={`${auctionManagementPath}/teams`} className="btn btn-outline-secondary btn-sm">
                Review team setup
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="d-flex flex-column gap-3">
        {renderTeamMeta()}

        <div className="card shadow-sm border-0">
          <div className="card-body d-flex flex-column gap-3">
            <div className="d-flex justify-content-between align-items-start gap-2">
              <div>
                <p className="eyebrow text-primary fw-semibold mb-1 text-uppercase small">Roster</p>
                <h2 className="h5 mb-0">Players on {activeTeam.name}</h2>
                <p className="text-muted mb-0">Review each player&apos;s image, role, and contact email.</p>
              </div>
              <span className="badge text-bg-light">{teamPlayers.length} players</span>
            </div>

            {renderPlayerList()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <PageShell containerClassName="p-0">
    <div className="py-4 d-flex flex-column gap-3">
      <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
        <div>
          <p className="eyebrow text-uppercase text-primary fw-semibold mb-1">Team details</p>
          <h1 className="h4 mb-1">Auction roster overview</h1>
          <p className="text-muted mb-0">View the full lineup, profile photos, and contact details for this team.</p>
        </div>
        <div className="d-flex flex-wrap gap-2 ms-auto">
          <Link to={`${auctionManagementPath}/online/start/shuffle`} className="btn btn-outline-secondary btn-sm">
            Back to auction start
          </Link>
          <Link to={`${auctionManagementPath}/teams`} className="btn btn-outline-primary btn-sm">
            Manage teams
          </Link>
        </div>
      </div>

      {renderContent()}
    </div>
  
    </PageShell>
  );
};

export default AuctionTeamDetailPage;
